import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { userAuthApi, paymentApi } from '../services/api';

const coreTechnologies = [
  {
    id: 'virtual-try-on',
    name: 'Virtual Try-On for Fashion & Clothing Industry',
    category: 'Fashion & Clothing',
    description:
      'AI fitting room deployment with garment rendering, shopper personalization, and analytics onboarding.',
    highlights: ['3D outfit previews', 'Size confidence scoring', 'Retail analytics dashboard'],
  },
  {
    id: 'pms-hospitality',
    name: 'Property Management System (PMS)',
    category: 'Tourism & Hospitality',
    description:
      'Hospitality operations suite covering reservations, guest workflows, and revenue optimization support.',
    highlights: ['Reservation automation', 'Guest profile intelligence', 'Revenue control workflows'],
  },
  {
    id: 'ecommerce-ai',
    name: 'eCommerce Platform',
    category: 'Consumer Retail',
    description:
      'Commerce acceleration package with personalization, product intelligence, and conversion optimization.',
    highlights: ['Smart recommendations', 'Catalog intelligence', 'Checkout uplift reporting'],
  },
  {
    id: 'ai-research-dev',
    name: 'AI Research & Development',
    category: 'JOFFON R&D',
    description:
      'Advanced AI research engagement for prototyping, model validation, and custom innovation planning.',
    highlights: ['Prototype roadmap', 'Model validation', 'R&D strategy sessions'],
  },
];

const recurringOptions = [
  { value: 'ONE_TIME', label: 'One-time payment' },
  { value: 'DAILY', label: 'Daily repeat request' },
  { value: 'WEEKLY', label: 'Weekly repeat request' },
  { value: 'MONTHLY', label: 'Monthly repeat request' },
];

const createInvoiceNumber = () => {
  const timestamp = Date.now().toString().slice(-8);
  return `JOF-${timestamp}`;
};

const toPayPalDescription = (value) => value.trim().slice(0, 120) || 'Payment for JOFFON Services';

const PayPalBadge = ({ compact = false }) => (
  <div className={`flex items-center ${compact ? 'gap-2' : 'gap-3'}`}>
    <div className={`relative ${compact ? 'h-7 w-8' : 'h-10 w-12'}`} aria-hidden="true">
      <span className={`absolute left-0 top-0 flex items-center justify-center rounded-lg bg-[#003087] font-black italic text-white shadow-[0_8px_18px_rgba(0,48,135,0.35)] ${compact ? 'h-7 w-5 text-sm' : 'h-10 w-7 text-xl'}`}>P</span>
      <span className={`absolute bottom-0 right-0 flex items-center justify-center rounded-lg bg-[#009cde] font-black italic text-white shadow-[0_8px_18px_rgba(0,156,222,0.35)] ${compact ? 'h-7 w-5 text-sm' : 'h-10 w-7 text-xl'}`}>P</span>
    </div>
    <div className="leading-none">
      <p className={`font-black italic tracking-tight text-white ${compact ? 'text-base' : 'text-xl'}`}>PayPal</p>
      {!compact && <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">Trusted checkout</p>}
    </div>
  </div>
);

const readCachedUser = () => {
  try {
    const cached = localStorage.getItem('currentUser');
    if (cached) return JSON.parse(cached);
  } catch (e) {}
  return null;
};

const SecurePaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const invoiceNumberParam = queryParams.get('invoiceNumber') || '';
  const queryName = decodeURIComponent(queryParams.get('name') || '');
  const queryCategory = decodeURIComponent(queryParams.get('category') || '');
  const queryDescription = decodeURIComponent(queryParams.get('description') || '');
  const queryAmount = Number.parseFloat(queryParams.get('amount') || '');

  const [invoiceData, setInvoiceData] = useState(null);

  const defaultTechnology =
    coreTechnologies.find(
      (t) => t.name === queryName || t.category === queryCategory,
    ) || null;

  // Customer details — sync init from localStorage so fields fill on first render
  const cached = readCachedUser();
  const [firstName, setFirstName] = useState(cached?.firstName || '');
  const [lastName, setLastName] = useState(cached?.lastName || '');
  const [companyName, setCompanyName] = useState(cached?.companyName || '');
  const [email, setEmail] = useState(cached?.email || '');
  const [phone, setPhone] = useState(cached?.phone || '');

  // Payment details
  const [amountInput, setAmountInput] = useState(
    Number.isFinite(queryAmount) && queryAmount > 0 ? queryAmount.toFixed(2) : ''
  );
  const [description, setDescription] = useState(queryDescription || '');
  const [recurringPlan, setRecurringPlan] = useState('');
  const [selectedTechnologyId, setSelectedTechnologyId] = useState(defaultTechnology?.id || '');
  const [invoiceNumber] = useState(createInvoiceNumber);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(!cached?.email);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [error, setError] = useState('');

  // Verify session, load user details, and optionally load invoice
  useEffect(() => {
    let cancelled = false;

    const verifyAndFetch = async () => {
      try {
        const response = await userAuthApi.getCurrentUser();
        if (cancelled) return;

        if (response.data?.authenticated) {
          const user = response.data.user;
          setFirstName(user.firstName || '');
          setLastName(user.lastName || '');
          setCompanyName(user.companyName || '');
          setEmail(user.email || '');
          setPhone(user.phone || '');
          try { localStorage.setItem('currentUser', JSON.stringify(user)); } catch (e) {}

          // If coming from a pending invoice link, fetch & auto-fill it
          if (invoiceNumberParam) {
            try {
              const invRes = await userAuthApi.getInvoice(invoiceNumberParam);
              if (!cancelled && invRes.data?.success) {
                const inv = invRes.data.invoice;
                setInvoiceData(inv);
                setAmountInput(Number(inv.totalAmount ?? inv.amount).toFixed(2));
                setDescription(inv.description || '');
                setRecurringPlan(inv.billingType);
                const matched = coreTechnologies.find(
                  (t) => t.name === inv.productName || t.category === inv.productCategory
                );
                if (matched) setSelectedTechnologyId(matched.id);
              }
            } catch (invErr) {
              if (!cancelled) setError('Could not load invoice details. Please try again.');
            }
          }
        } else {
          try { localStorage.removeItem('currentUser'); } catch (e) {}
          // Paying a specific invoice still requires login (it's tied to one
          // user's account). A general payment can proceed as a guest —
          // the customer detail fields just stay blank for manual entry.
          if (invoiceNumberParam) {
            navigate(`/login?next=${encodeURIComponent(location.pathname + location.search || '/secure-payment')}`);
          }
        }
      } catch (err) {
        if (!cancelled) {
          // On a network/CORS error (err.response is undefined) use the cached
          // user if one exists so the page stays usable when the API is down.
          const fallback = readCachedUser();
          if (!err.response && fallback?.email) {
            setFirstName(fallback.firstName || '');
            setLastName(fallback.lastName || '');
            setCompanyName(fallback.companyName || '');
            setEmail(fallback.email || '');
            setPhone(fallback.phone || '');
          } else if (invoiceNumberParam) {
            navigate(`/login?next=${encodeURIComponent(location.pathname + location.search || '/secure-payment')}`);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    verifyAndFetch();
    return () => { cancelled = true; };
  }, [navigate, location.pathname, location.search]); // eslint-disable-line

  const isInvoicePayment = Boolean(invoiceData);
  const selectedTechnology = coreTechnologies.find((t) => t.id === selectedTechnologyId) || defaultTechnology || {};
  const selectedRecurringOption = recurringOptions.find((o) => o.value === recurringPlan) || {};
  const parsedAmount = Number.parseFloat(amountInput);
  const hasValidAmount = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const productName = selectedTechnology.name || 'Custom Payment';
  const productCategory = selectedTechnology.category || 'Uncategorized';

  const handleProceed = () => {
    if (!firstName.trim() || !lastName.trim() || !companyName.trim() || !email.trim()) {
      setError('Please fill in your first name, last name, company name, and email address.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!hasValidAmount) {
      setError('Enter a valid payment amount before continuing.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!recurringPlan) {
      setError('Please select a billing request option.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError('');
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePayment = async () => {
    if (!hasValidAmount || !recurringPlan || !firstName.trim() || !lastName.trim() || !companyName.trim() || !email.trim()) {
      setError('Please complete all required fields.');
      return;
    }

    setPaymentLoading(true);
    setError('');

    try {
      const orderResponse = await paymentApi.createOrder({
        amount: Number(parsedAmount.toFixed(2)),
        currency: 'USD',
        description: toPayPalDescription(description),
        invoiceNumber,
        customId: `${recurringPlan}|${productCategory}`,
        productName,
        customerInfo: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          companyName: companyName.trim(),
          email: email.trim(),
          phone: phone.trim(),
        },
      });

      const { id: orderId, approveLink } = orderResponse.data;
      if (!orderId) throw new Error('Failed to create PayPal order');
      if (!approveLink) throw new Error('PayPal approval link not available');

      window.location.href = approveLink;
    } catch (err) {
      setError(err.response?.data?.details || err.message || 'Payment failed');
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#04050c] text-white">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#04050c] text-white">
      <Navbar />

      <div className="relative overflow-hidden border-b border-cyan-500/20 bg-gradient-to-b from-[#0a0f21] via-[#060713] to-[#04050c]">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[820px] h-[420px] bg-cyan-500/20 blur-[130px] rounded-full pointer-events-none"></div>
        <div className="absolute -top-[180px] -right-[160px] w-[540px] h-[540px] bg-fuchsia-500/20 blur-[140px] rounded-full pointer-events-none"></div>
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8 relative z-10">
          <button
            onClick={() => navigate('/')}
            className="mb-8 inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </button>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">Secure Payment</h1>
          <p className="text-slate-300/80">Complete your order securely with PayPal</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        {error && step === 1 && (
          <div className="mb-8 p-4 bg-red-500/20 border border-red-500 text-red-200 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Invoice banner */}
        {isInvoicePayment && invoiceData && (
          <div className="mb-8 rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-5">
            <p className="text-xs uppercase tracking-widest text-yellow-400 mb-1">Paying Invoice</p>
            <p className="text-white font-semibold text-lg">{invoiceData.productName}</p>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-300">
              <span>Invoice: <span className="font-mono text-yellow-300">{invoiceData.invoiceNumber}</span></span>
              <span>Billing: <span className="text-yellow-300">{invoiceData.billingType}</span></span>
              {invoiceData.dueDate && (
                <span>Due: <span className="text-yellow-300">{new Date(invoiceData.dueDate).toLocaleDateString()}</span></span>
              )}
            </div>
            {invoiceData.description && (
              <p className="mt-2 text-sm text-slate-400">{invoiceData.description}</p>
            )}
            {invoiceData.feeAmount > 0 && (
              <div className="mt-3 flex flex-wrap gap-4 border-t border-yellow-500/20 pt-3 text-sm text-slate-300">
                <span>Amount: <span className="text-white">${Number(invoiceData.amount).toFixed(2)}</span></span>
                <span>Fee ({invoiceData.feePercent}%): <span className="text-white">${Number(invoiceData.feeAmount).toFixed(2)}</span></span>
                <span>Total: <span className="font-semibold text-yellow-300">${Number(invoiceData.totalAmount).toFixed(2)}</span></span>
              </div>
            )}
          </div>
        )}

        {step === 1 ? (
          <div className="space-y-8">
            {/* 1. Customer Details */}
            <div className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-[#0a0f21] to-[#060713] p-8">
              <div className="flex items-center gap-4 mb-8 border-b border-white/10 pb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cyan-500/20 border border-cyan-500/40 shadow-[0_0_15px_rgba(34,211,238,0.2)]">
                  <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white tracking-wide">1. Customer Details</h2>
              </div>
              <div className="grid gap-4 md:grid-cols-2 mb-4">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">First Name <span className="text-fuchsia-400">*</span></label>
                  <input type="text" value={firstName} onChange={e => { setFirstName(e.target.value); setError(''); }} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="John" />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Last Name <span className="text-fuchsia-400">*</span></label>
                  <input type="text" value={lastName} onChange={e => { setLastName(e.target.value); setError(''); }} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="Doe" />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-slate-300 text-sm font-medium mb-2">Company Name <span className="text-fuchsia-400">*</span></label>
                <input type="text" value={companyName} onChange={e => { setCompanyName(e.target.value); setError(''); }} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="JOFFON L.L.C." />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Email Address <span className="text-fuchsia-400">*</span></label>
                  <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="john@example.com" />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Phone Number <span className="text-slate-500 font-normal">(Optional)</span></label>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:bg-white/10 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(34,211,238,0.15)]" placeholder="+1 (555) 000-0000" />
                </div>
              </div>
            </div>

            {/* 2. Payment Details */}
            <div className="rounded-2xl border border-fuchsia-500/30 bg-gradient-to-br from-[#0c0817] to-[#060713] p-8">
              <div className="flex items-center gap-4 mb-8 border-b border-white/10 pb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 shadow-[0_0_15px_rgba(217,70,239,0.2)]">
                  <svg className="w-6 h-6 text-fuchsia-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white tracking-wide">2. Payment Details</h2>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Amount (USD) <span className="text-fuchsia-400">*</span></label>
                  <div className="relative flex items-center">
                    <span className="absolute left-5 text-2xl font-bold text-fuchsia-400">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={amountInput}
                      onChange={e => { if (!isInvoicePayment) { setAmountInput(e.target.value); if (error) setError(''); } }}
                      readOnly={isInvoicePayment}
                      className={`w-full rounded-xl border border-white/10 bg-black/40 pl-11 pr-4 py-4 text-3xl font-bold text-fuchsia-400 outline-none transition-all duration-300 ${isInvoicePayment ? 'opacity-70 cursor-not-allowed' : 'focus:border-fuchsia-400 focus:bg-black/60 focus:shadow-[0_0_20px_rgba(217,70,239,0.15)]'}`}
                      placeholder="0.00"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-400">Type the amount you want to charge in USD.</p>
                </div>
                <div>
                  <label htmlFor="payment-description" className="block text-slate-300 text-sm font-medium mb-2">Description</label>
                  <textarea
                    id="payment-description"
                    value={description}
                    onChange={e => { if (!isInvoicePayment) setDescription(e.target.value); }}
                    readOnly={isInvoicePayment}
                    rows={4}
                    className={`w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-all duration-300 ${isInvoicePayment ? 'opacity-70 cursor-not-allowed' : 'focus:bg-white/10 focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]'}`}
                    placeholder="Enter the service description to appear in PayPal order details"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">Repeated billing request</label>
                  {isInvoicePayment ? (
                    <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white opacity-70">
                      {recurringOptions.find(o => o.value === recurringPlan)?.label || recurringPlan}
                      <span className="ml-2 text-xs text-slate-500">(set by invoice)</span>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-slate-400 mb-3">
                        Select whether this invoice should be marked as daily, weekly, or monthly. PayPal checkout still runs as a single payment.
                      </p>
                      <div className="relative">
                        <select
                          value={recurringPlan}
                          onChange={e => setRecurringPlan(e.target.value)}
                          className="w-full appearance-none rounded-xl border border-white/10 bg-[#0d1226] px-4 py-3 text-sm text-white outline-none transition-all duration-300 focus:border-fuchsia-400 focus:shadow-[0_0_15px_rgba(217,70,239,0.15)]"
                        >
                          <option value="" disabled>Select billing request...</option>
                          {recurringOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-400">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* 3. Core Technologies — hidden when paying a specific invoice */}
            {!isInvoicePayment && (
            <div className="rounded-2xl border border-pink-500/30 bg-gradient-to-br from-[#120a16] to-[#060713] p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 border-b border-white/10 pb-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pink-500/20 border border-pink-500/40 shadow-[0_0_15px_rgba(236,72,153,0.2)]">
                    <svg className="w-6 h-6 text-pink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white tracking-wide">3. Core Technologies</h2>
                    <p className="text-sm text-slate-400 mt-1">Select the package you want to pay for.</p>
                  </div>
                </div>
                <div className="hidden sm:block rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs uppercase tracking-[0.2em] text-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.2)]">
                  PayPal Ready
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {coreTechnologies.map((t) => {
                  const isSelected = t.id === selectedTechnologyId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSelectedTechnologyId(t.id)}
                      className={`group rounded-2xl border p-5 text-left transition-all duration-300 ${isSelected ? 'border-pink-400 bg-pink-500/10 shadow-[0_0_22px_rgba(236,72,153,0.18)] -translate-y-1' : 'border-white/10 bg-white/[0.03] hover:border-pink-400/40 hover:bg-pink-500/5 hover:-translate-y-0.5'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className={`text-xs uppercase tracking-[0.2em] mb-2 transition-colors ${isSelected ? 'text-pink-300' : 'text-slate-400 group-hover:text-pink-300/70'}`}>{t.category}</p>
                          <h3 className="text-lg font-semibold text-white">{t.name}</h3>
                        </div>
                        <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-sm flex-shrink-0 transition-all ${isSelected ? 'border-pink-300 bg-pink-400/20 text-pink-200 scale-110 shadow-[0_0_10px_rgba(236,72,153,0.4)]' : 'border-slate-600 text-slate-500 group-hover:border-slate-400'}`}>
                          {isSelected ? '✓' : ''}
                        </span>
                      </div>
                      <p className="mt-3 text-sm leading-relaxed text-slate-300">{t.description}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {t.highlights.map((h) => (
                          <span key={h} className={`rounded-full border px-3 py-1 text-xs transition-colors ${isSelected ? 'border-pink-500/30 bg-pink-500/10 text-pink-200' : 'border-white/10 bg-white/5 text-slate-300'}`}>
                            {h}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            <button
              onClick={handleProceed}
              className="w-full px-6 py-4 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(34,211,238,0.2)] hover:shadow-[0_0_25px_rgba(34,211,238,0.4)] text-lg"
            >
              Proceed to Order Summary
            </button>
          </div>
        ) : (
          <div className="space-y-8 max-w-2xl mx-auto">
            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500 text-red-200 rounded-lg text-sm">{error}</div>
            )}

            <div className="rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-[#0a0f21] to-[#060713] p-8">
              <h2 className="text-2xl font-bold text-white mb-8 border-b border-white/10 pb-4">Order Summary</h2>
              <div className="space-y-6 text-slate-300">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Invoice Number</span>
                  <span className="font-mono text-cyan-300 bg-cyan-500/10 px-2 py-1 rounded w-fit sm:w-auto">{invoiceNumber}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Customer</span>
                  <span className="text-white sm:text-right">{firstName} {lastName}<br /><span className="text-sm text-slate-400">{email}</span></span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Product Selected</span>
                  <span className="text-white sm:text-right sm:max-w-xs">{productName}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Description</span>
                  <span className="text-white sm:text-right sm:max-w-xs">{description || '--'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-slate-400">Billing Request</span>
                  <span className="text-white">{selectedRecurringOption.label || '--'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pt-6 border-t border-white/20 mt-4 gap-2">
                  <span className="text-lg font-bold text-white">Total Amount</span>
                  <span className="text-4xl font-bold text-cyan-400">${parsedAmount.toFixed(2)} <span className="text-lg text-slate-400">USD</span></span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handlePayment}
                disabled={paymentLoading}
                className="w-full px-6 py-4 bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]"
              >
                {paymentLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <PayPalBadge compact />
                    <span className="text-lg">Pay with PayPal</span>
                  </div>
                )}
              </button>
              <button
                onClick={() => { setStep(1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={paymentLoading}
                className="w-full px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50"
              >
                Back to Edit Details
              </button>
            </div>

            <p className="text-xs text-slate-500 text-center">Secured by PayPal | SSL Encrypted | USA Payments</p>
          </div>
        )}
      </main>

      <footer className="border-t border-fuchsia-500/20 bg-[#04050b] mt-10 py-16">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="text-left">
            <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-fuchsia-200 to-pink-200 font-extrabold tracking-widest mb-2 text-lg">JOFFON L.L.C.</h4>
            <p className="text-slate-400/70 text-sm font-light">3400 Cottage Way, Ste G2 #18859</p>
            <p className="text-slate-400/70 text-sm font-light">Sacramento, California 95825</p>
          </div>
          <div className="text-center md:text-right">
            <p className="text-slate-300/80 text-sm mb-1 font-light">Email: info@joffon.com</p>
            <p className="text-slate-300/80 text-sm mb-6 font-light">Phone: 925-482-6198</p>
            <p className="text-slate-500 text-[10px] uppercase tracking-[0.2em] font-bold">
              &copy; {new Date().getFullYear()} JOFFON AI. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecurePaymentPage;
